/**
 * TVG-142: Per-venue room outlines for the Table Plan canvas, true-to-scale in
 * the canvas cm unit (1 unit = 1 cm, see canvas-geometry.ts).
 *
 * TVG-372: every room is redrawn from Richard Bray's sketches (25 and
 * 30 September 2026). Each one is drawn the right way round, wide side across
 * the screen, so nothing is rotated any more. Richard's figures are used as
 * given; a wall he did not measure is scaled from his drawing.
 *
 * A room is defined by an ordered list of wall vertices (clockwise). The builder
 * derives the bounding box, a fill path, and a dimension label for every wall
 * segment (length in metres, drawn along that side) so staff and couples can
 * read the room size at a glance. Openings (doorways, gaps, bar/serving gaps)
 * are flagged so they render as a break in the wall.
 *
 * Fixed features (kitchen, bar, toilets, doors, outside areas) are text labels
 * only. They never stop a table being placed.
 *
 * Red House Barn has no room yet, so it has no outline and no Table Plan tab.
 */

export type Point = readonly [number, number];

/**
 * TVG-372: a fixed feature is a label drawn just inside the room, next to the
 * wall or opening it belongs to. It has no footprint, so it never blocks a table.
 */
export interface RoomFeature {
  label: string;
  x: number;
  y: number;
  /** SVG text-anchor, so labels against a side wall stay inside the room. */
  anchor: 'start' | 'middle' | 'end';
}

export interface RoomWall {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Pre-computed dimension label + placement along the outside of the wall. */
  label: string;
  labelX: number;
  labelY: number;
  /** Degrees to rotate the label so it runs along the wall. */
  angle: number;
  /** Doorway / gap / serving opening — rendered as a break in the wall. */
  opening: boolean;
}

/**
 * TVG-373: a dotted line drawn inside the room, such as the sliding doors that
 * close off the Milling Barn DJ alcove. A label only — it never blocks a table.
 */
export interface RoomDivider {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label: string;
}

export interface RoomOutline {
  name: string;
  /** Bounding box in cm. Drives the per-venue SVG viewBox. */
  width: number;
  height: number;
  polygon: ReadonlyArray<Point>;
  /** Closed fill path for the room interior. */
  fillPath: string;
  walls: ReadonlyArray<RoomWall>;
  features: ReadonlyArray<RoomFeature>;
  dividers: ReadonlyArray<RoomDivider>;
}

interface WallSpec {
  /** Edge indices (0-based, in vertex order) that are openings, not solid wall. */
  openings?: ReadonlyArray<number>;
  /**
   * Override the auto length label for specific edges (e.g. "Door 1.2m"). An
   * empty string hides the label on a short jog that would only add clutter.
   */
  labels?: Readonly<Record<number, string>>;
  features?: ReadonlyArray<RoomFeature>;
  dividers?: ReadonlyArray<RoomDivider>;
}

function metresLabel(lengthCm: number): string {
  const m = Math.round((lengthCm / 100) * 10) / 10;
  return `${m}m`;
}

/**
 * Twice the signed area (shoelace). Positive when the vertices run clockwise
 * on screen (y grows downward), which is how every room is written.
 */
function signedArea(points: ReadonlyArray<Point>): number {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    area += x1 * y2 - x2 * y1;
  }
  return area;
}

/**
 * Build a RoomOutline from clockwise wall vertices. Each polygon edge becomes a
 * labelled wall; the label sits just outside the wall (offset along the outward
 * normal) and is rotated to run along it.
 */
function buildRoom(name: string, vertices: ReadonlyArray<Point>, spec: WallSpec = {}): RoomOutline {
  const xs = vertices.map((p) => p[0]);
  const ys = vertices.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  // Normalise so the bounding box starts at (0, 0).
  const polygon: Point[] = vertices.map(([x, y]) => [x - minX, y - minY] as Point);
  const width = maxX - minX;
  const height = maxY - minY;
  // TVG-372: the outward side of each wall comes from the winding order, so a
  // label on a step or alcove wall stays outside the room. The old
  // away-from-the-centroid guess put some of them inside concave rooms.
  const winding = signedArea(polygon) >= 0 ? 1 : -1;

  const openings = new Set(spec.openings ?? []);
  const labelOverrides = spec.labels ?? {};
  const LABEL_OFFSET = 30;

  const walls: RoomWall[] = polygon.map((a, i) => {
    const b = polygon[(i + 1) % polygon.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy);
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;

    // Outward normal for a clockwise (on screen) outline.
    const nx = (winding * dy) / (len || 1);
    const ny = (winding * -dx) / (len || 1);

    // Keep labels upright: vertical-ish walls rotate to read bottom-to-top.
    const vertical = Math.abs(dy) > Math.abs(dx);
    const angle = vertical ? -90 : 0;

    return {
      x1: a[0],
      y1: a[1],
      x2: b[0],
      y2: b[1],
      label: labelOverrides[i] ?? metresLabel(len),
      labelX: mx + nx * LABEL_OFFSET,
      labelY: my + ny * LABEL_OFFSET,
      angle,
      opening: openings.has(i),
    };
  });

  const fillPath =
    polygon.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ') + ' Z';

  const features = (spec.features ?? []).map((f) => ({
    ...f,
    x: f.x - minX,
    y: f.y - minY,
  }));

  const dividers = (spec.dividers ?? []).map((d) => ({
    ...d,
    x1: d.x1 - minX,
    y1: d.y1 - minY,
    x2: d.x2 - minX,
    y2: d.y2 - minY,
  }));

  return { name, width, height, polygon, fillPath, walls, features, dividers };
}

/**
 * TVG-372: Ashton Lodge, from Richard's sketch of 30 September 2026. Top wall
 * 15.8 m, then the 1 m kitchen door at the top right. Right wall 4.1 / bar
 * opening 1.6 / 4.1, so the room is 9.8 m deep. Garden wall 8.5 / garden door
 * 1.9 / 6.5, left to right. The dance floor area on the left is about 2 m wide
 * and runs down to the garden wall: the 6.6 m left wall, a 1 m piece below it,
 * then along the bottom the 1 m outdoor kitchen door and 1 m of wall. Above it
 * the sketch cuts a 2.2 m deep notch out of the top-left corner, with a door in
 * its 2 m return. 2.2 + 6.6 + 1 = 9.8 m.
 *
 * The garden runs (16.9 m) are scaled to the 16.8 m the top wall and kitchen
 * door give, which is under 1% and still labels 8.5 / 1.9 / 6.5. The sketch also
 * shows a further 1 m of wall after the kitchen door that the agreed figures
 * leave out; it is not drawn and is flagged for Richard.
 */
const ASHTON_LODGE = buildRoom(
  'Ashton Lodge Country House',
  [
    [200, 0], // top wall left → right: 15.8, kitchen door 1.0
    [1780, 0],
    [1880, 0],
    [1880, 410], // right wall top → bottom: 4.1, bar 1.6, 4.1
    [1880, 570],
    [1880, 980],
    [1234, 980], // garden wall right → left: 6.5, garden door 1.9, 8.5
    [1045, 980],
    [200, 980],
    [100, 980], // 1.0 of wall, then the outdoor kitchen door 1.0
    [0, 980],
    [0, 880], // left wall bottom → top: 1.0, 6.6
    [0, 220],
    [50, 220], // top-left return: 0.5, door 1.0, 0.5
    [150, 220],
    [200, 220], // then 2.2 up to the top wall
  ],
  {
    openings: [1, 3, 6, 9, 13],
    labels: {
      1: 'Door 1m',
      3: 'Bar 1.6m',
      6: 'Garden door 1.9m',
      9: 'Door 1m',
      12: '',
      13: 'Door 1m',
      14: '',
    },
    features: [
      { label: '↑ Kitchen', x: 1860, y: 50, anchor: 'end' },
      { label: 'Bar →', x: 1840, y: 490, anchor: 'end' },
      { label: 'Garden ↓', x: 1140, y: 930, anchor: 'middle' },
      { label: 'Outdoor Kitchen ↓', x: 20, y: 930, anchor: 'start' },
      { label: 'Dance floor', x: 100, y: 550, anchor: 'middle' },
    ],
  },
);

/**
 * TVG-372: Milling Barn, from Richard's sketch of 25 September 2026. The outline
 * is unchanged apart from the end walls, which were the wrong way round: the
 * right wall on the bar side is 6.13 m and the left wall on the entrance side is
 * 6.2 m. Top wall 8.5 / kitchen door 1.2 / 6.1 / disabled toilet gap 0.9 / 1.7;
 * bottom right 6.6; DJ alcove 3.8 m wide × 3.5 m deep; bottom left 7.8. The
 * 7.8 m bottom-left run is 0.2 m short of the top, so the bottom-left corner
 * sits in at (20, 620) to close the outline honestly.
 *
 * TVG-373, from Richard Bray on 27 October 2026: the 0.9 m disabled toilet gap
 * on the top wall is an opening (drawn as a gap, like every opening), and the
 * DJ alcove is closed off by large sliding doors, drawn as a dotted line across
 * its mouth. Neither blocks a table.
 */
const MILLING_BARN = buildRoom(
  'Milling Barn',
  [
    [0, 0], // top wall left → right: 8.5, kitchen door 1.2, 6.1, gap 0.9, 1.7
    [850, 0],
    [970, 0],
    [1580, 0],
    [1670, 0],
    [1840, 0],
    [1840, 613], // right wall (bar) 6.13
    [1180, 613], // bottom right → left: 6.6
    [1180, 963], // DJ alcove down 3.5
    [800, 963], // DJ alcove across 3.8
    [800, 613], // DJ alcove up 3.5
    [20, 620], // bottom left 7.8, then the entrance wall 6.2 back to [0, 0]
  ],
  {
    openings: [1, 3],
    labels: { 1: 'Door 1.2m', 3: 'Gap 0.9m', 5: '6.13m' },
    features: [
      { label: '↑ Kitchen', x: 910, y: 50, anchor: 'middle' },
      { label: '↑ Disabled Toilet', x: 1625, y: 50, anchor: 'middle' },
      { label: 'Bar →', x: 1800, y: 260, anchor: 'end' },
      { label: '← Entrance', x: 50, y: 513, anchor: 'start' },
      { label: 'DJ', x: 990, y: 800, anchor: 'middle' },
    ],
    dividers: [{ x1: 800, y1: 613, x2: 1180, y2: 613, label: 'Sliding doors' }],
  },
);

/**
 * TVG-372: The Great Barn, from Richard's sketch of 25 September 2026. Mirrored
 * left to right so the Aynho Barn end is on the left and the kitchen on the
 * right. Both long sides from the Aynho Barn end: 6.0 / 3.8 / 7.3 (17.1 m). Aynho
 * Barn end, top → bottom: 2.5 / door 1.4 / 2.8. Kitchen
 * end, top → bottom: 3.8 / kitchen opening 1.2 / 1.7. The Car Park door is in the
 * top 3.8 m section and the Garden door in the bottom one.
 */
const GREAT_BARN = buildRoom(
  'The Great Barn',
  [
    [0, 0], // top wall left → right: 6.0, 3.8 (Car Park door), 7.3
    [600, 0],
    [980, 0],
    [1710, 0],
    [1710, 380], // kitchen end top → bottom: 3.8, kitchen opening 1.2, 1.7
    [1710, 500],
    [1710, 670],
    [980, 670], // bottom wall right → left: 7.3, 3.8 (Garden door), 6.0
    [600, 670],
    [0, 670],
    [0, 390], // Aynho Barn end bottom → top: 2.8, door 1.4, 2.5
    [0, 250],
  ],
  {
    openings: [4, 10],
    // The kitchen opening keeps a plain length label so it clears the 1.7 m
    // run below it; the "Kitchen →" feature label names it.
    labels: { 10: 'Door 1.4m' },
    features: [
      { label: '↑ Car Park', x: 790, y: 50, anchor: 'middle' },
      { label: 'Garden ↓', x: 790, y: 620, anchor: 'middle' },
      { label: 'Kitchen →', x: 1670, y: 440, anchor: 'end' },
      { label: '← Aynho Barn', x: 40, y: 320, anchor: 'start' },
    ],
  },
);

/**
 * TVG-372: Plum Park Manor, rebuilt from Richard's sketch of 25 September 2026.
 * Top wall, left → right: a 1.2 m door at the top-left corner, 13.3, kitchen
 * door 1.0, 2.6. Right wall 3.1 / dance floor opening 5.8 / 3.1 (12.0 m), with
 * the dance floor stepping 2.8 m out of the opening. Left side: 8.7 m down the
 * bar side, a 3.6 m step out to the left by the toilets, then an unmeasured run
 * to the floor (12.0 − 8.7 = 3.3 m, scaled from the drawing).
 *
 * The bottom runs (6.3 / entrance 2.2 / 7.7 / outdoor kitchen 2.2 / 3.2 = 21.6 m)
 * are scaled to the top walls (3.6 + 1.2 + 13.3 + 1.0 + 2.6 = 21.7 m), as agreed
 * until Richard confirms. That is under 0.5%, so every label reads as he wrote it.
 */
const PLUM_PARK = buildRoom(
  'Plum Park Manor',
  [
    [360, 0], // top wall left → right: door 1.2, 13.3, kitchen door 1.0, 2.6
    [480, 0],
    [1810, 0],
    [1910, 0],
    [2170, 0],
    [2170, 310], // right wall top → bottom: 3.1, then the dance floor step-out
    [2450, 310], //   2.8 out, 5.8 along, 2.8 back
    [2450, 890],
    [2170, 890],
    [2170, 1200], //   3.1
    [1849, 1200], // bottom wall right → left (scaled): 3.2, outdoor kitchen 2.2,
    [1628, 1200], //   7.7, entrance 2.2, 6.3
    [854, 1200],
    [633, 1200],
    [0, 1200],
    [0, 870], // left side bottom → top: 3.3 (unmeasured), 3.6 step, 8.7 bar side
    [360, 870],
  ],
  {
    openings: [0, 2, 10, 12],
    labels: {
      0: 'Door 1.2m',
      2: 'Door 1m',
      10: 'Outdoor kitchen 2.2m',
      12: 'Entrance 2.2m',
    },
    features: [
      { label: '↑ Kitchen', x: 1860, y: 50, anchor: 'middle' },
      { label: '← Bar', x: 400, y: 435, anchor: 'start' },
      { label: '← Toilets', x: 40, y: 1035, anchor: 'start' },
      { label: 'Entrance ↓', x: 743, y: 1150, anchor: 'middle' },
      { label: 'Outdoor Kitchen ↓', x: 1738, y: 1150, anchor: 'middle' },
      { label: 'Dance floor', x: 2220, y: 600, anchor: 'middle' },
    ],
  },
);

/**
 * Venue name → outline. Keys match Venue.name in the database. A venue with no
 * outline (Red House Barn, or an unknown venue) has no room and no Table Plan.
 */
export const ROOM_OUTLINES: Readonly<Record<string, RoomOutline>> = {
  'Milling Barn': MILLING_BARN,
  'The Great Barn': GREAT_BARN,
  'Ashton Lodge Country House': ASHTON_LODGE,
  'Plum Park Manor': PLUM_PARK,
};

/**
 * TVG-372: the room for a venue, or null when the venue has none. There is no
 * longer a Milling Barn fallback for Red House Barn or unknown venues.
 */
export function outlineForVenue(venueName: string | null | undefined): RoomOutline | null {
  if (venueName && ROOM_OUTLINES[venueName]) {
    return ROOM_OUTLINES[venueName];
  }
  return null;
}

/** TVG-372: true when the venue has a Table Plan room (drives the tab). */
export function hasRoomOutline(venueName: string | null | undefined): boolean {
  return outlineForVenue(venueName) !== null;
}

/**
 * TVG-372: an empty room used only so the canvas has something to bind to if
 * it is ever rendered for a venue with no room. The Table Plan tab is hidden for
 * those venues, so staff never see it.
 */
export const NO_ROOM_OUTLINE: RoomOutline = {
  name: '',
  width: 0,
  height: 0,
  polygon: [],
  fillPath: '',
  walls: [],
  features: [],
  dividers: [],
};

/** Standard-library even-odd point-in-polygon test (ray casting). */
export function isPointInPolygon(x: number, y: number, polygon: ReadonlyArray<Point>): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    const intersects =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * TVG-218: how far (cm) each corner sample is nudged inward toward the box
 * centre before the point-in-polygon test, so a tile may sit flush to a wall.
 * A corner landing exactly on a wall (x=0, y=0, …) is rejected by the strict
 * ray-cast; nudging it a hair inside lets flush placements count as inside,
 * removing the old ≥50 cm inset that kept perimeter tables off the walls. Kept
 * in sync with App\Support\TablePlanRooms::FLUSH_EPSILON.
 */
export const FLUSH_EPSILON = 1;

/**
 * True when an axis-aligned table footprint fits inside a (possibly concave)
 * room. We require all four corners and the centre to lie within the wall
 * polygon — enough to keep a tile honestly inside the room, including its
 * alcoves, without a full edge-intersection test. Corners are nudged inward by
 * FLUSH_EPSILON so a tile sitting flush to a wall still counts as inside.
 */
export function boxFitsInRoom(
  box: { x1: number; y1: number; x2: number; y2: number },
  outline: RoomOutline,
): boolean {
  const cx = (box.x1 + box.x2) / 2;
  const cy = (box.y1 + box.y2) / 2;
  const nudge = (v: number, centre: number): number =>
    v + Math.sign(centre - v) * FLUSH_EPSILON;
  const samples: ReadonlyArray<Point> = [
    [nudge(box.x1, cx), nudge(box.y1, cy)],
    [nudge(box.x2, cx), nudge(box.y1, cy)],
    [nudge(box.x2, cx), nudge(box.y2, cy)],
    [nudge(box.x1, cx), nudge(box.y2, cy)],
    [cx, cy],
  ];
  return samples.every(([x, y]) => isPointInPolygon(x, y, outline.polygon));
}

/** Pairwise AABB overlap test for two placed table footprints. */
export function boxesOverlap(
  a: { x1: number; y1: number; x2: number; y2: number },
  b: { x1: number; y1: number; x2: number; y2: number },
): boolean {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
}
